import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { Stack, TextField, Theme, Typography } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { FormEvent, ReactNode } from 'react';
import CircularProgressButton from '../Form/CircularProgressButton';
import DisplayAlertErrors from '../Form/DisplayAlertErrors';
import { LinkSent } from '../LinkSent/LinkSent';
import { LINK_SENT_TAIL } from '../MagicLinkCopy/linkSentCopy';
import { StatusKnockoutTile } from '../StatusKnockoutTile/StatusKnockoutTile';
import { StickyActionBar } from '../StickyActionBar/StickyActionBar';
import { SignInRequired } from './SignInRequired';

type Copy = { title: string; body: string; button?: string };

/** Board slot 29 (approved copy, sole consumer): the resend action's own replacement notice,
 *  shown under the "send me a new link" button. */
export const EARLIER_LINK_REPLACEMENT_NOTICE =
  'This replaces any link we have already sent you, including one you have not opened.';

const DEFAULT_COPY: Copy = {
  title: 'Sorry! This link is no longer valid',
  body: "VerifiMe invite links expire after 15 minutes, may only be used once, and become invalid if a newer link was sent for security reasons. Confirm your email address and we'll send you a new link.",
  button: 'Send me a new link',
};

const RECOVERY_COPY: Record<string, Copy> = {
  'wrong-account': {
    title: 'Incorrect email address',
    body: "The email address you used doesn't match the invitation. Please make sure you're using the same email you used for the organisation you're verifying for, and we'll send you a sign-in link.",
    button: 'Send me a sign-in link',
  },
  'server-error': {
    title: 'Something went wrong on our end',
    body: "We couldn't complete your sign in, and this one is on us. Confirm your email address and try again.",
    button: 'Try again',
  },
  failed: {
    title: "This link can't be used",
    body: "Something went wrong completing your sign in. Confirm your email address and we'll send you a sign-in link.",
    button: 'Send me a sign-in link',
  },
  cancelled: {
    title: 'Sign in was cancelled',
    body: "Confirm your email address and we'll send you a sign-in link to continue.",
    button: 'Send me a sign-in link',
  },
  'broken-link': {
    title: "We don't recognise this link",
    body: "This invitation link isn't recognised, so we can't recover it here. Please contact the organisation that invited you and ask them to send a new invitation.",
    // no button -> terminal, no form
  },
};

/** The copy a reason selects. An absent or unrecognised reason selects the default copy. The
 *  reason can be any string a URL carries, so only the table's own keys count: `toString` and
 *  the other keys every object inherits are not reasons. */
export function copyForReason(reason?: string): Copy {
  if (reason === undefined || !Object.hasOwn(RECOVERY_COPY, reason)) return DEFAULT_COPY;
  return RECOVERY_COPY[reason];
}

// Associates the field's own label (below) with its input via htmlFor/id, so the accessible name
// comes from a real label element rather than the placeholder. A fixed literal, matching the rest
// of this component (which renders exactly one email field per mount).
const EMAIL_FIELD_ID = 'recover-invitation-email';

export type RecoverInvitationProps = {
  reason?: string;
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: (e: FormEvent) => void;
  isSubmitting: boolean;
  errorMessage?: string;
  attribution?: ReactNode;
  sentToEmail?: string;
  /** When the magic link this screen reports on stops working. Only meaningful once
   *  `sentToEmail` is set, but required unconditionally, matching GoogleSsoSignUpForm's own
   *  `expiresAt` (see that component's doc comment for the full reasoning): only the caller that
   *  fired the send (useRecoverInvitation, via its own `sentAt`) knows when that was, and this
   *  component must never derive it itself - that would recompute from "now" on every render,
   *  creeping the shown expiry forward as the customer watches. Forwarded straight into
   *  LinkSent's own `expiresAt`. */
  expiresAt: Date | string;
  /** Optional slot rendered on the sent screen for a resend affordance (e.g. LinkSentResend).
   *  Caller-supplied, following the same rendered-ReactNode-slot pattern as `attribution` above;
   *  this component places it, but never constructs it. */
  resendAction?: ReactNode;
  /** The one action of the `sign-in-required` reason: takes the customer to the normal sign-in.
   *  Used by that reason only; every other reason submits the form instead. */
  onSignIn?: () => void;
  'data-testid'?: string;
};

export default function RecoverInvitation({
  reason,
  email,
  onEmailChange,
  onSubmit,
  isSubmitting,
  errorMessage,
  attribution,
  sentToEmail,
  expiresAt,
  resendAction,
  onSignIn,
  'data-testid': dataTestId,
}: RecoverInvitationProps) {
  // Before the sent screen on purpose: a resend that answers 409 can leave `sentToEmail` set, and
  // the sent screen has no way to show the reason.
  if (reason === 'sign-in-required') {
    return (
      <SignInRequired onSignIn={onSignIn} attribution={attribution} data-testid={dataTestId} />
    );
  }

  if (sentToEmail) {
    // Task 4 ("Link sent, one screen", settled 2026-09-17): this branch's previous "Check your
    // email" heading and "We've sent a sign-in link to {email}. Open it on this device to
    // continue." sentence are both superseded and gone, replaced by the shared LinkSent shell
    // every "we sent you a magic link" screen now composes (see GoogleSsoSignUpForm.tsx for the
    // same rewrite on /sign-up). The board's ruling: one screen serves every route, so the lead
    // line must be true whether the customer is signing up, recovering an invitation, or signing
    // in - "on this device" is only true for the last of those, so it goes rather than finding a
    // new home. Every visible string below is one of the approved constants; this component
    // authors none of it.
    return (
      <Stack
        data-testid={dataTestId}
        sx={(theme: Theme) => ({
          gap: theme.semantic.spacing['2lg'],
          padding: tokens.spacing2lg,
          maxWidth: tokens.sizeSm,
          alignItems: 'center',
          textAlign: 'center',
        })}
      >
        <LinkSent
          heading="Email sent!"
          leadLine="We've sent a link to"
          email={sentToEmail}
          expiryLabel="The email link will be valid until"
          expiresAt={expiresAt}
          tail={LINK_SENT_TAIL}
          resend={resendAction}
        />
        {errorMessage && <DisplayAlertErrors errors={[{ message: errorMessage }]} />}
      </Stack>
    );
  }

  const copy = copyForReason(reason);
  const isTerminal = reason === 'broken-link';

  if (isTerminal) {
    return (
      <Stack
        data-testid={dataTestId}
        sx={(theme: Theme) => ({
          gap: theme.semantic.spacing['2lg'],
          padding: tokens.spacing2lg,
          maxWidth: tokens.sizeSm,
          alignItems: 'center',
          textAlign: 'center',
        })}
      >
        {/* D7/C4: both failure states are red. 'broken-link' uses the info glyph (not the error
            glyph), but the SAME permanent danger colour as every other reason: giving "we don't
            recognise this link" a grey treatment would invent a severity the design does not
            have. */}
        <StatusKnockoutTile
          icon={<InfoIcon sx={{ fontSize: 40 }} />}
          data-testid={dataTestId && `${dataTestId}-status-tile`}
        />
        <Stack gap={tokens.spacingXs}>
          <Typography variant="h4">{copy.title}</Typography>
          <Typography variant="body1">{copy.body}</Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      data-testid={dataTestId}
      component="form"
      onSubmit={onSubmit}
      sx={(theme: Theme) => ({
        gap: theme.semantic.spacing['2lg'],
        padding: tokens.spacing2lg,
        maxWidth: tokens.sizeSm,
        alignItems: 'center',
        textAlign: 'center',
      })}
    >
      <StatusKnockoutTile
        icon={<ErrorIcon sx={{ fontSize: 40 }} />}
        data-testid={dataTestId && `${dataTestId}-status-tile`}
      />
      <Stack gap={tokens.spacingXs}>
        <Typography variant="h4">{copy.title}</Typography>
        <Typography variant="body1">{copy.body}</Typography>
      </Stack>
      {attribution}
      {/* C2 exception: the field's own label sits left above its input, and the field's own
          input text stays left too. This is the board's one deliberate departure from this
          screen's centred alignment. Form controls do not inherit text-align from an ancestor in
          any environment, real browser included: verified in Chromium, an <input> nested under a
          div with an explicit textAlign (centred or left) still computes 'start', its own
          user-agent default, regardless of what the ancestor declares. So a plain
          sx={{ textAlign: 'left' }} on the TextField root would be a no-op in production too, not
          just unobservable in this test. Targeting the input slot's own class
          (`& .MuiInputBase-input`) is the only selector that actually reaches the element and
          takes effect, and it still holds if a future MUI version changes the input's own
          default. The label is a plain Typography, not TextField's own `label` prop: MUI's
          built-in label either floats over the input's border or shrinks into it, neither of
          which is the board's separate, always-visible, bold left-aligned line above the field.
          It is still a real <label htmlFor>, not a caption beside an unlabelled input: that is
          what lets the field's accessible name come from the label rather than from nothing, once
          the placeholder (which the accessible-name computation ignores) stops carrying it. */}
      <Stack gap={tokens.spacingXs} sx={{ width: '100%' }}>
        <Typography
          component="label"
          htmlFor={EMAIL_FIELD_ID}
          variant="body1"
          sx={{
            display: 'block',
            textAlign: 'left',
            fontWeight: tokens.fontWeightSemibold,
          }}
        >
          Confirm your email address
        </Typography>
        <TextField
          id={EMAIL_FIELD_ID}
          type="email"
          required
          fullWidth
          // An approved example address already shipping elsewhere in this package
          // (GoogleSsoSignUpForm.tsx), not a newly authored literal: no example address for this
          // screen exists in the copy module, and inventing one here would be authoring copy.
          placeholder="name@example.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          sx={{ '& .MuiInputBase-input': { textAlign: 'left' } }}
        />
      </Stack>
      {/* D8: slot 29 renders on every non-terminal reason (default/wrong-account/server-error/
          failed/cancelled), inside the sticky bar with the button (gate-2 assertion 4:
          containment, not proximity). 'broken-link' is terminal (no button, no bar), so it never
          reaches this branch. */}
      {/* contentInset="0px": this Stack already carries its own 24px padding
          (`tokens.spacing2lg` above) on every side, matching `MagicLinkContinue.tsx`'s own Stack
          (also 24px at `xs`). Leaving the bar's own default contentInset in place stacked a
          second, redundant 24px inset on top of the Stack's, measured directly against a real
          viewport at 48px total rather than the board's 24px. Passing 0px here matches how
          StepWrapper's own consent caller already handles the identical situation (its content
          supplies its own inset; see the comment in StickyActionBar.tsx). */}
      {/* parentPaddingBottom={tokens.spacing2lg}: negates this same 24px Stack padding on the
          bottom too, mirroring `parentPaddingX`'s own mechanism. Without it, the bar's own 28px
          bottom padding stacked on top of this Stack's 24px padding-bottom instead of being
          pulled down through it, leaving the bar 24px above the board's position. */}
      <StickyActionBar
        data-testid="recover-invitation-actionbar"
        contentInset="0px"
        parentPaddingBottom={tokens.spacing2lg}
      >
        <CircularProgressButton
          // Fixed rather than derived from `copy.button`: the visible label changes per `reason`
          // (RECOVERY_COPY/DEFAULT_COPY above), but every reason submits through this same one
          // action, so ADR-0071's id must stay stable across all of them.
          data-action="request-sign-in-link"
          isShowProgress={isSubmitting}
          size="large"
          variant="contained"
          type="submit"
          fullWidth
        >
          {copy.button}
        </CircularProgressButton>
        <Typography variant="body2" color="text.tertiary">
          {EARLIER_LINK_REPLACEMENT_NOTICE}
        </Typography>
        {errorMessage && <DisplayAlertErrors errors={[{ message: errorMessage }]} />}
      </StickyActionBar>
    </Stack>
  );
}
